// Third Party Imports
import React from 'react';
import ReactDOM from 'react-dom';
import {InMemoryCache} from 'apollo-cache-inmemory';
import {ApolloClient} from 'apollo-client';
import {ApolloLink} from 'apollo-link';
import {HttpLink} from 'apollo-link-http';
import {ConnectedRouter as Router} from 'connected-react-router';
import {createBrowserHistory} from 'history';
import {polyfill as smoothscrollPolyfill} from 'smoothscroll-polyfill';
// First Party Imports
import TrackFocus from '@homeaway/track-focus';
import AbacusClientUtils from '@homeaway/abacus-client-utils';
// Relative Imports
import setupDebugLog from './utils/debugLog';
import {createStoreFromSerializedStore} from './store';
import Root from './Root';
import tripActions from './actions/tripActions';
import edapActions from './actions/edapActions';
import {determineTripDataFromWindow} from './utils/tripData';
import guestSelectorActions from './actions/guestSelectorActions';
import csrfTokenLink from './utils/csrfTokenLink';
import getExperiments from './utils/experimentsHelper';
import loadLocaleData from './utils/loadLocaleData';
import {isTripBoardOperation} from '@homeaway/react-trip-board-button';
import {evaluateExperiment} from './utils/abacusHelper';
import {VRBO_TGA_PDP} from './abacus/experiments';
import {isBexApiOperation} from './utils/bexApiHelper';

function logMetricSandwich() {
    if (typeof XMLHttpRequest !== 'undefined' && typeof ha !== 'undefined') {
        const oReq = new XMLHttpRequest();
        let pageLoadData = '?metricSandwich=true';

        const metrics = [
            {name: 'responseEnded', label: 'responseEnded', data: 'duration'},
            {name: 'index.initialize', label: 'indexInitialize', data: 'startTime'},
            {name: 'index.start.import.start', label: 'indexStartImportStart', data: 'startTime'},
            {name: 'domInteractive', label: 'domInteractive', data: 'duration'},
            {name: 'index.start.import.done', label: 'indexStartImportDone', data: 'startTime'},
            {name: 'index.start.hydrate.start', label: 'indexStartHydrateStart', data: 'startTime'},
            {name: 'container.constructor', label: 'containerConstructor', data: 'startTime'},
            {name: 'component.did.mount', label: 'componentDidMount', data: 'startTime'},
            {name: 'index.start.hydrate.done', label: 'indexStartHydrateDone', data: 'startTime'},
            {name: 'domComplete', label: 'domComplete', data: 'duration'},
            {name: 'loadEventStart', label: 'loadEventStart', data: 'duration'},
            {name: 'loadEventEnd', label: 'loadEventEnd', data: 'duration'}
        ];
        const navigationStart =
            ha && ha.userTiming && ha.userTiming.timing && ha.userTiming.timing.navigationStart;
        metrics.forEach((metric) => {
            const analytic = ha.userTiming.getEntriesByName(metric.name);
            let analyticRounded = Math.ceil(analytic.length ? analytic[0][metric.data] : 0);

            // Even though userTiming is told to measure the above, they are not all
            // being calculated, and available in getEntriesByName.  However, we can
            // still figure it out from navigationStart
            if (!analyticRounded && navigationStart) {
                const rawAnalytic =
                    (ha.userTiming.timing && ha.userTiming.timing[metric.name]) || 0;

                if (rawAnalytic) {
                    analyticRounded = rawAnalytic - navigationStart;
                }
            }

            pageLoadData += `&${metric.label}=${analyticRounded}`;
        });

        oReq.open('GET', `/pdp/logger${pageLoadData}`, true);
        oReq.setRequestHeader('x-homeaway-site', window.__SITE_CONTEXT__.name);
        oReq.send();
    }
}
/* global ha */
(async function() {
    /* eslint-disable */
    ha.userTiming.measure(
        'responseEnded',
        'navigationStart',
        'responseEnd',
        'domInteractive',
        'domContentLoaded',
        'domComplete',
        'loadEventStart',
        'loadEventEnd'
    );
    // This is the point after all the needed files are downloaded and eval'ed
    ha.userTiming.mark('index.initialize');
    /* eslint-enable */

    // Initial state as set by the server-side render
    const initialState = window.__INITIAL_STATE__;

    // Request state either set by Fastly or the server side render
    const requestState = window.__REQUEST_STATE__;

    const history = createBrowserHistory({
        basename: window.__SITE_CONTEXT__ ? window.__SITE_CONTEXT__.basePath : ''
    });

    // block on the /experiments request if the visitor is in variant 1 of the caching test
    if (requestState.cacheMode === 1) {
        try {
            initialState.abacusTestsReducer = await getExperiments();
        } catch (e) {
            // do nothing, and just use whatever `initialState.abacusTestsReducer` is currently populated with
        }
    }

    AbacusClientUtils.initialize(initialState.abacusTestsReducer);

    const store = createStoreFromSerializedStore(initialState, requestState, history);
    // Key information for the systemReducer is now filled in during store initialization.
    // So, set this up after the store is ready to roll.
    setupDebugLog(window, store.getState().systemReducer.toJS(), requestState);

    // Expose the public assets path so that Webpack can fetch assets correctly
    __webpack_public_path__ = store.getState().systemReducer.get('webpackPublicPath'); // eslint-disable-line

    // load our trip data from the browser and notify interested consumers via the tripDetailsChanged action
    // (which in turn will fetch the price for the given trip details)
    const tripData = determineTripDataFromWindow(window);

    const tripPayload = {
        adults: tripData.adultsCount,
        children: tripData.childrenCount,
        hasPets: tripData.hasPets,
        arriveDateText: tripData.arriveDate,
        departDateText: tripData.departDate,
        isFamilySegment: tripData.isFamilySegment,
        arrivedWithoutDates: !tripData.arriveDate && !tripData.departDate
    };
    store.dispatch(tripActions.tripDetailsChanged(tripPayload, true));

    store.dispatch(
        guestSelectorActions.guestSelectorDataUpdated({
            adults: tripData.adultsCount,
            children: tripData.childrenCount,
            pets: tripData.hasPets
        })
    );

    // trigger the 'pageview' now that all trip data has been loaded and the page's ADL has been updated
    store.dispatch(edapActions.triggerEdapEvent('pageview'));
    store.dispatch(edapActions.triggerEdapPropertySummaryPresented());

    // set up keyboard focus tracking
    const trackFocus = new TrackFocus();
    trackFocus.bindEvents();

    smoothscrollPolyfill();

    const linkHeaders = {
        'x-homeaway-site': window && window.__SITE_CONTEXT__.name,
        'x-ha-location': store.getState().systemReducer.get('locationData')
    };

    // initialize apollo client
    const shouldUseTga = evaluateExperiment({name: VRBO_TGA_PDP, variant: 1});
    const client = new ApolloClient({
        link: ApolloLink.from([
            csrfTokenLink,
            ApolloLink.split(
                isBexApiOperation,
                new HttpLink({
                    uri: '/expedia/graphql',
                    headers: linkHeaders
                }),
                ApolloLink.split(
                    isTripBoardOperation,
                    new HttpLink({
                        uri: '/traveler/scratchpad/api/graphql',
                        credentials: 'same-origin',
                        headers: linkHeaders
                    }),
                    ApolloLink.split(
                        () => shouldUseTga,
                        new HttpLink({
                            uri: '/mobileapi/graphql',
                            credentials: 'same-origin',
                            headers: linkHeaders
                        }),
                        new HttpLink({
                            uri: '/pdp/graphql',
                            credentials: 'same-origin',
                            headers: linkHeaders
                        })
                    )
                )
            )
        ]),
        cache: new InMemoryCache(),
        name: 'web_pdp',
        version: '2.0'
    });

    const start = (ListingPageComponent) => {
        ha.userTiming.mark('index.start.import.done');

        const app = (
            <Root
                client={client}
                store={store}
                listingPageComponent={ListingPageComponent.default}
                siteContext={window.__SITE_CONTEXT__}
                router={({children}) => (
                    <Router history={history} noInitialPop>
                        {children}
                    </Router>
                )}
            />
        );

        ha.userTiming.mark('index.start.hydrate.start');

        ReactDOM.hydrate(app, document.getElementById('root'));
        ha.userTiming.mark('index.start.hydrate.done');

        // Want to capture loadEventEnd, so need to wait until load event.
        window.addEventListener('load', () => {
            // IE9+
            setTimeout(() => {
                logMetricSandwich();
            }, 0); // 0, since we just want it to defer.
        });
    };

    ha.userTiming.mark('index.start.import.start');
    const {systemReducer} = initialState;
    const isMobile = systemReducer.get('isMobile');

    let importBundle = isMobile
        ? import('./components/ListingPage/ListingPageMobile')
        : import('./components/ListingPage/ListingPageDesktop');

    await loadLocaleData(store.getState().systemReducer.get('displayLocale'));

    return importBundle.then(start);
})();
